import React, { useEffect, useState } from 'react';
import {
  Box, Container, Typography, Button, Grid, Card, CardContent, CardActions,
  IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Paper,
  CircularProgress, Alert, Tooltip, LinearProgress, Tabs, Tab,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  CalendarMonth as CalendarIcon, Build as BuildIcon,
  MeetingRoom as RoomIcon, DirectionsCar as VehicleIcon,
  Inventory as EquipmentIcon, Timeline as TimelineIcon,
  LocationOn as LocationIcon, CheckCircle as AvailableIcon,
  Cancel as UnavailableIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import resourceService, { Resource, ResourceType } from '../services/resourceService';

const getTypeIcon = (icon: string) => {
  switch (icon) {
    case 'meeting_room': case 'room': return <RoomIcon />;
    case 'directions_car': case 'vehicle': return <VehicleIcon />;
    case 'inventory': case 'equipment': return <EquipmentIcon />;
    default: return <BuildIcon />;
  }
};

const ResourcesPage: React.FC = () => {
  const navigate = useNavigate();
  const [resources, setResources] = useState<Resource[]>([]);
  const [resourceTypes, setResourceTypes] = useState<ResourceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [tabValue, setTabValue] = useState(0);

  // Resource dialog
  const [resourceDialog, setResourceDialog] = useState(false);
  const [editingResource, setEditingResource] = useState<Resource | null>(null);
  const [resourceForm, setResourceForm] = useState({
    name: '', description: '', resourceTypeId: '', capacity: 1,
    capacityUnit: 'units', location: '', color: '#3B82F6',
  });

  // Resource type dialog
  const [typeDialog, setTypeDialog] = useState(false);
  const [typeForm, setTypeForm] = useState({ name: '', description: '', icon: 'build', color: '#6B7280' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [res, types] = await Promise.all([
        resourceService.getResources(),
        resourceService.getResourceTypes(),
      ]);
      setResources(res);
      setResourceTypes(types);
    } catch (err: any) {
      setError('Failed to load resources');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateResource = async () => {
    try {
      await resourceService.createResource(resourceForm);
      setResourceDialog(false);
      setResourceForm({ name: '', description: '', resourceTypeId: '', capacity: 1, capacityUnit: 'units', location: '', color: '#3B82F6' });
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data || 'Failed to create resource');
    }
  };

  const handleUpdateResource = async () => {
    if (!editingResource) return;
    try {
      await resourceService.updateResource(editingResource.id, resourceForm);
      setResourceDialog(false);
      setEditingResource(null);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update resource');
    }
  };

  const handleDeleteResource = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;
    try {
      await resourceService.deleteResource(id);
      loadData();
    } catch (err: any) {
      setError('Failed to delete resource');
    }
  };

  const handleCreateType = async () => {
    try {
      await resourceService.createResourceType(typeForm);
      setTypeDialog(false);
      setTypeForm({ name: '', description: '', icon: 'build', color: '#6B7280' });
      loadData();
    } catch (err: any) {
      setError('Failed to create resource type');
    }
  };

  const openCreateDialog = () => {
    setEditingResource(null);
    setResourceForm({ name: '', description: '', resourceTypeId: resourceTypes[0]?.id || '', capacity: 1, capacityUnit: 'units', location: '', color: '#3B82F6' });
    setResourceDialog(true);
  };

  const openEditDialog = (resource: Resource, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingResource(resource);
    setResourceForm({
      name: resource.name, description: resource.description || '',
      resourceTypeId: resource.resourceTypeId, capacity: resource.capacity,
      capacityUnit: resource.capacityUnit, location: resource.location || '',
      color: resource.color || '#3B82F6',
    });
    setResourceDialog(true);
  };

  const filteredResources = filterType === 'all'
    ? resources
    : resources.filter(r => r.resourceTypeId === filterType);

  if (loading) {
    return <Container sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Container>;
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Resources</Typography>
          <Typography variant="body2" color="text.secondary">
            {resources.length} resources, {resourceTypes.length} types
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<TimelineIcon />}
            onClick={() => navigate('/resources/timeline')}>
            Timeline
          </Button>
          <Button variant="outlined" onClick={() => setTypeDialog(true)}>
            Manage Types
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}
            disabled={resourceTypes.length === 0}>
            Add Resource
          </Button>
        </Box>
      </Box>

      {/* Filter by type */}
      {resourceTypes.length > 0 && (
        <Paper sx={{ p: 1.5, mb: 3 }}>
          <Tabs value={filterType} onChange={(_, v) => setFilterType(v)} variant="scrollable">
            <Tab label={`All (${resources.length})`} value="all" />
            {resourceTypes.map(t => (
              <Tab key={t.id} label={`${t.name} (${t.resourceCount})`} value={t.id}
                icon={getTypeIcon(t.icon)} iconPosition="start" sx={{ minHeight: 48 }} />
            ))}
          </Tabs>
        </Paper>
      )}

      {/* Resource Cards */}
      {filteredResources.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <BuildIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No resources yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {resourceTypes.length === 0
              ? 'Create a resource type first, then add resources'
              : 'Add your first resource to start managing bookings'}
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={resourceTypes.length === 0 ? () => setTypeDialog(true) : openCreateDialog}>
            {resourceTypes.length === 0 ? 'Create Resource Type' : 'Add Resource'}
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredResources.map(resource => (
            <Grid item xs={12} sm={6} md={4} key={resource.id}>
              <Card sx={{ height: '100%', borderTop: `4px solid ${resource.color || '#6B7280'}`, cursor: 'pointer' }}
                onClick={() => navigate(`/resources/${resource.id}/calendar`)}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {getTypeIcon(resource.resourceTypeIcon)}
                      <Typography variant="h6">{resource.name}</Typography>
                    </Box>
                    {resource.isAvailable
                      ? <Chip icon={<AvailableIcon />} label="Available" color="success" size="small" />
                      : <Chip icon={<UnavailableIcon />} label="Unavailable" color="error" size="small" />
                    }
                  </Box>

                  <Chip label={resource.resourceTypeName} size="small" variant="outlined" sx={{ mb: 1 }} />

                  {resource.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {resource.description}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                    <Typography variant="body2">
                      Capacity: <strong>{resource.capacity} {resource.capacityUnit}</strong>
                    </Typography>
                    {resource.location && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">{resource.location}</Typography>
                      </Box>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      Active bookings: {resource.activeBookingsCount}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
                  <Button size="small" startIcon={<CalendarIcon />}
                    onClick={(e) => { e.stopPropagation(); navigate(`/resources/${resource.id}/calendar`); }}>
                    Calendar
                  </Button>
                  <Box>
                    <IconButton size="small" onClick={(e) => openEditDialog(resource, e)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error"
                      onClick={(e) => { e.stopPropagation(); handleDeleteResource(resource.id); }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create/Edit Resource Dialog */}
      <Dialog open={resourceDialog} onClose={() => setResourceDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingResource ? 'Edit Resource' : 'Add Resource'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Name" value={resourceForm.name}
                onChange={e => setResourceForm({ ...resourceForm, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" multiline rows={2} value={resourceForm.description}
                onChange={e => setResourceForm({ ...resourceForm, description: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={resourceForm.resourceTypeId} label="Type"
                  onChange={e => setResourceForm({ ...resourceForm, resourceTypeId: e.target.value })}>
                  {resourceTypes.map(t => (
                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Capacity" type="number" value={resourceForm.capacity}
                onChange={e => setResourceForm({ ...resourceForm, capacity: parseInt(e.target.value) || 1 })} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Unit</InputLabel>
                <Select value={resourceForm.capacityUnit} label="Unit"
                  onChange={e => setResourceForm({ ...resourceForm, capacityUnit: e.target.value })}>
                  <MenuItem value="units">Units</MenuItem>
                  <MenuItem value="pieces">Pieces/day</MenuItem>
                  <MenuItem value="hours">Hours/day</MenuItem>
                  <MenuItem value="seats">Seats</MenuItem>
                  <MenuItem value="kg">Kg/day</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Location" value={resourceForm.location}
                onChange={e => setResourceForm({ ...resourceForm, location: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResourceDialog(false)}>Cancel</Button>
          <Button variant="contained" disabled={!resourceForm.name || !resourceForm.resourceTypeId}
            onClick={editingResource ? handleUpdateResource : handleCreateResource}>
            {editingResource ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Resource Type Dialog */}
      <Dialog open={typeDialog} onClose={() => setTypeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Resource Type</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Type Name" value={typeForm.name}
                placeholder="e.g., Machine, Meeting Room, Vehicle"
                onChange={e => setTypeForm({ ...typeForm, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" value={typeForm.description}
                onChange={e => setTypeForm({ ...typeForm, description: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Icon</InputLabel>
                <Select value={typeForm.icon} label="Icon"
                  onChange={e => setTypeForm({ ...typeForm, icon: e.target.value })}>
                  <MenuItem value="build">Machine</MenuItem>
                  <MenuItem value="room">Meeting Room</MenuItem>
                  <MenuItem value="vehicle">Vehicle</MenuItem>
                  <MenuItem value="equipment">Equipment</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Color</InputLabel>
                <Select value={typeForm.color} label="Color"
                  onChange={e => setTypeForm({ ...typeForm, color: e.target.value })}>
                  <MenuItem value="#3B82F6">Blue</MenuItem>
                  <MenuItem value="#10B981">Green</MenuItem>
                  <MenuItem value="#F59E0B">Orange</MenuItem>
                  <MenuItem value="#EF4444">Red</MenuItem>
                  <MenuItem value="#8B5CF6">Purple</MenuItem>
                  <MenuItem value="#6B7280">Gray</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTypeDialog(false)}>Cancel</Button>
          <Button variant="contained" disabled={!typeForm.name} onClick={handleCreateType}>Create</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ResourcesPage;
